import React, { useState, useMemo, useEffect } from "react";
import { observer } from "mobx-react-lite";
import emissionViewStore from "../../../Store/marco/emissionViewStore";
import { emissionFactorAPI } from "../../API/emissionFactorAPI";
import styles from "../../modals/styles/EmissionActivityModal.module.css";
import { IncertEFDec } from "../../components/IncertEFDec";
import loadingSvg from "../../../Assets/images/tube-spinner.svg";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { validEF } from "../utils/EFIncertController";
import { handleEditEF } from "../utils/EFIncertController";
// ---------- debounce 工具 ----------
function debounce(fn, delay = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

export const EmissionFactorIncertModal = observer(() => {
  const store = emissionViewStore;
  const {
    _handleSetEFIncertFormData,
    _handleSetIsShowDataEntryUnitMismatchModal,
    _handleSetEFCheckId,
    _handleSetLandEFCheckId,
    _handleSetEmissionActivityModalLoading,
    _handleSetIsShowEFIncertModal,
  } = store;
  const { modifyEFCheck } = emissionFactorAPI;
  const { t } = useTranslation("ActivityMain");

  let cat = store.selectedTab.charAt(0);

  cat = `cat${cat}`;

  const [EFDecData, setEFDecData] = useState([]);
  // ---------- 驗證係數 ----------
  const validEmissionFactor = async (target) => {
    if (!store.EFIncertFormData.EFId && store.EFIncertFormData.landEFId) return;
    setEFDecData([]);

    try {
      _handleSetEmissionActivityModalLoading(true);
      const response = await validEF();

      if (response) {
        _handleSetEFIncertFormData(
          false,
          target === "Transport" ? "landEFTypeId" : "EFTypeId",
          response.emissionTypeId || null
        );

        if (cat !== "cat3") {
          const checkDataEntryMatch = await modifyEFCheck(cat, {
            id: store.emissionFactorFormData?.id,
            isTransport4_3: false,
            emissionFactorTypeId: store.EFIncertFormData?.EFTypeId,
            emissionFactorId: store.EFIncertFormData?.EFId,
          });

          if (checkDataEntryMatch === false) {
            target === "Transport"
              ? _handleSetLandEFCheckId(true)
              : _handleSetEFCheckId(store.emissionFactorFormData?.id);
          }
        }

        _handleSetEFIncertFormData(false, "valided", true);
        setEFDecData([response]);
      } else {
        _handleSetEFIncertFormData(false, "valided", false);
        setEFDecData([]);
      }
    } catch (error) {
      console.error("Error fetch validation", error);
    } finally {
      _handleSetEmissionActivityModalLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (store.EFCheckId) {
      _handleSetIsShowDataEntryUnitMismatchModal(true);
      return;
    }

    await handleEditEF();
  };

  const handleClose = () => {
    _handleSetIsShowEFIncertModal(false);
    _handleSetEFCheckId("");
    _handleSetLandEFCheckId(false);
  };

  // ---------- debounce 包裝 ----------
  const debouncedValidEF = useMemo(
    () => debounce(validEmissionFactor, 600), // 延遲 500ms
    []
  );

  useEffect(() => {
    return () => {
      setEFDecData([]);
    };
  }, []);

  return (
    <div className={styles.bgc}>
      <div
        className={`${styles.container} ${
          store.emissionActivityModalLoading >= 1 ? styles.isLoading : ""
        }`}
      >
        {store.emissionActivityModalLoading >= 1 && (
          <div className={styles.loading}>
            <img
              className={styles.loadingImg}
              src={loadingSvg}
              alt="loadingSvg"
            />
          </div>
        )}

        <div className={styles.title}>
          {t("Edit")} {t(store.selectedTab)}
          <span className={styles.close} onClick={handleClose}>
            <CloseIcon />
          </span>
        </div>

        <div className={styles.main}>
          {/* 名稱型號 */}
          <div className={styles.inputWrap}>
            <label>{t("Name") + " / " + t("Number")}</label>
            <input placeholder={store.emissionFactorFormData.name} disabled />
          </div>

          {/**第一個係數 */}
          <div className={styles.EFInsertInputWrap}>
            <label>{t("Select Emission Factor")}</label>
            <input
              className={styles.eFInput}
              type="number"
              onChange={(e) => {
                _handleSetEFIncertFormData(false, "EFId", e.target.value);
                debouncedValidEF();
              }}
            />
            {store.EFCheckId ? (
              <p className={styles.EFWarningWords} style={{ color: "red" }}>
                {/**EF 與數據設定衝突提醒 */}
                {t(
                  "The emission factor and the data input unit are inconsistent."
                )}
              </p>
            ) : (
              <></>
            )}
          </div>

          {/**第二個係數 ( 4.3 ) */}
          {store.emissionFactorFormData.haveTransport >= 1 ? (
            <div className={styles.EFInsertInputWrap}>
              <label>{t("Select Emission Factor ( Transportation )")}</label>
              <input
                className={styles.eFInput}
                type="number"
                onChange={(e) => {
                  _handleSetEFIncertFormData(false, "landEFId", e.target.value);
                  debouncedValidEF("Transport");
                }}
              />
              {store.landEFCheckId ? (
                <p className={styles.EFWarningWords} style={{ color: "red" }}>
                  {/**EF 與數據設定衝突提醒 */}
                  {t(
                    "The emission factor and the data input unit are inconsistent."
                  )}
                </p>
              ) : (
                <></>
              )}
            </div>
          ) : null}

          {/* --- EF 說明區塊 --- */}
          {EFDecData.length > 0 ? (
            <IncertEFDec parentData={EFDecData} />
          ) : (
            <p style={{ color: "black", width: "600px" }}>
              {t("No emission factor found.")}
            </p>
          )}
        </div>

        {/* --- Footer --- */}
        <div className={styles.footer}>
          <div className={styles.cancel} onClick={handleClose}>
            {t("Cancel")}
          </div>

          <div
            className={`${styles.add} ${
              EFDecData.length <= 0 ? styles.disabled : ""
            }`}
            style={{ display: "inline-block" }} // ✅ 保持 inline 顯示
            onClick={handleSubmit}
          >
            {t("Edit")}
          </div>
        </div>
      </div>
    </div>
  );
});
